import { Link } from 'react-router';
import { useBusinessMetrics } from '../../api/queries';
import { QueryBoundary } from '../../components/QueryBoundary';
import { PageHeader } from '../../components/PageHeader';
import { useTranslation } from '../../i18n/I18nProvider';
import { formatMoney, formatNumber } from '../../lib/format';
import { Card, CardHeader, SkeletonCard, StatTile } from '../../ui';

const LINKS = [
  { to: '/admin/korisnici', key: 'shell.users' },
  { to: '/admin/proizvodi', key: 'shell.products' },
  { to: '/admin/placanja', key: 'shell.payments' },
  { to: '/admin/sadrzaj', key: 'shell.content' },
  { to: '/admin/analitika', key: 'shell.analytics' },
];

/**
 * Admin početna. Brojke dolaze iz `GET /v1/admin/metrics` (`BusinessMetrics` u
 * `@st/contract`) — istog izvora koji koristi i /admin/analitika, pa dva ekrana
 * ne mogu pokazati različitu konverziju.
 *
 * Prije je ovdje stajao `useAdminAnalytics()` s ručno napisanim tipom koji API
 * nikad nije vraćao (`/v1/admin/analytics` vraća retke `analytics_daily`), pa je
 * `currency` bio `undefined` i `Intl.NumberFormat` je rušio cijelu stranicu.
 */
export function AdminDashboard() {
  const { t, locale } = useTranslation();
  const query = useBusinessMetrics();

  return (
    <>
      <PageHeader eyebrow={t('shell.admin')} title={t('portal.dashboard')} />
      <QueryBoundary query={query} skeleton={<SkeletonCard />}>
        {(m) => (
          <div className="flex flex-col gap-5">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="admin-kpis">
              <StatTile label={t('admin.conversion')} value={m.conversionPct} unit="%" />
              <StatTile
                label={t('admin.activeUsers')}
                value={formatNumber(m.users.active30, locale)}
              />
              <StatTile
                label={t('admin.churn')}
                value={m.churnPct}
                unit="%"
                hint={t('admin.churnHint')}
              />
              <StatTile
                label={t('metrics.revenue30')}
                value={formatMoney(m.revenue.last30Cents, m.revenue.currency, locale)}
              />
            </div>
            <Card>
              <CardHeader title={t('shell.admin')} />
              <ul className="grid gap-2 sm:grid-cols-2">
                {LINKS.map((l) => (
                  <li key={l.to}>
                    <Link
                      to={l.to}
                      className="block rounded-md border border-line-subtle px-4 py-3 text-[14.5px] text-ink transition-colors hover:bg-bg-alt"
                    >
                      {t(l.key)}
                    </Link>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        )}
      </QueryBoundary>
    </>
  );
}
